import React, { useState, useEffect, useRef } from "react";
import { MessageSquare, X, Send, AlertCircle } from "lucide-react";
import { db } from "../../firebase/config";
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  doc, 
  orderBy 
} from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";

export default function POTeamsChatModal({ isOpen, onClose, po }) {
  if (!isOpen || !po) return null;

  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [activeChat, setActiveChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const capitalizeName = (name) => {
    if (!name) return "";
    return name.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  const currentUserFullName = capitalizeName(currentUser?.displayName || currentUser?.email?.split("@")[0] || "Me");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // 1. Subscribe to the PO channel in /chats
  useEffect(() => {
    setLoading(true);
    const chatsQuery = query(
      collection(db, "chats"),
      where("poId", "==", po.id)
    );

    const unsubscribeChat = onSnapshot(chatsQuery, (snapshot) => {
      if (!snapshot.empty) {
        const chatData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
        setActiveChat(chatData);
      } else {
        setActiveChat(null);
        // Fallback to static PO chatThread array if no channel is created yet
        setMessages(po.chatThread || []);
        setLoading(false);
      }
    });

    return () => unsubscribeChat();
  }, [po.id, po.chatThread]);

  // 2. Subscribe to messages of the PO channel if found
  useEffect(() => {
    if (!activeChat) return;

    const messagesQuery = query(
      collection(db, "chats", activeChat.id, "messages"),
      orderBy("timestamp", "asc")
    );

    const unsubscribeMsgs = onSnapshot(messagesQuery, (snapshot) => {
      const msgsList = [];
      snapshot.forEach((doc) => {
        msgsList.push({ id: doc.id, ...doc.data() });
      });
      setMessages(msgsList);
      setLoading(false);
    }, (err) => {
      console.error("Error loading PO channel messages:", err);
      setLoading(false);
    });

    return () => unsubscribeMsgs();
  }, [activeChat]);

  // 3. Send Message
  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    const text = messageInput.trim();
    if (!text) return;

    setMessageInput("");

    const timestamp = new Date().toISOString();
    const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const newMsg = {
      sender: currentUserFullName,
      senderEmail: currentUser?.email || "anonymous",
      senderId: currentUser?.uid || "anonymous",
      text,
      timestamp,
      time: timeStr
    };

    try {
      let chatId = activeChat?.id;

      // Create channel on the fly if it doesn't exist yet
      if (!chatId) {
        const poNum = po.poNumber || `PO-${po.id.substring(0, 5)}`;
        const clientName = po.customerName ? po.customerName.toLowerCase().replace(/[^a-z0-9]/g, "-") : "client";
        const channelName = `po-${poNum.toLowerCase()}-${clientName}`;

        const newChatDoc = await addDoc(collection(db, "chats"), {
          name: channelName,
          type: "channel",
          poId: po.id,
          createdAt: timestamp,
          lastMessage: text,
          lastMessageTime: timeStr,
          lastMessageTimestamp: timestamp
        });
        chatId = newChatDoc.id;
      } else {
        // Update existing chat doc
        const chatDocRef = doc(db, "chats", chatId);
        await updateDoc(chatDocRef, {
          lastMessage: text,
          lastMessageTime: timeStr,
          lastMessageTimestamp: timestamp
        });
      }

      // Add message to Firestore chats/messages subcollection
      await addDoc(collection(db, "chats", chatId, "messages"), newMsg);

      // Append message to PO document's chatThread array
      const poDocRef = doc(db, "pos", po.id);
      const currentThread = po.chatThread || [];
      const msgToStore = {
        sender: currentUserFullName,
        text,
        time: timeStr,
        timestamp
      };
      await updateDoc(poDocRef, {
        chatThread: [...currentThread, msgToStore]
      });

    } catch (err) {
      console.error("Failed to send message from PO modal:", err);
    }
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.75)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1100,
      backdropFilter: "blur(4px)"
    }}>
      <div 
        className="card glass-card animate-scale-up" 
        style={{
          width: "550px",
          maxWidth: "90%",
          height: "80vh",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          padding: 0,
          border: "1px solid var(--border-color)",
          backgroundColor: "rgba(20, 24, 33, 0.95)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)"
        }}
      >
        {/* Modal Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1rem 1.5rem",
          borderBottom: "1px solid var(--border-color)",
          backgroundColor: "rgba(10, 12, 18, 0.4)"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <MessageSquare size={18} style={{ color: "var(--primary)" }} />
            <div>
              <h3 style={{ margin: 0, color: "var(--primary)", fontSize: "1.1rem" }}>
                EST Chat Log: {po.poNumber || po.id.substring(0, 5)}
              </h3>
              <p style={{ margin: "0.15rem 0 0 0", fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                Permanent thread log attached to order.
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Message Stream */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: "1.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          backgroundColor: "rgba(10, 12, 18, 0.2)"
        }}>
          {messages.length > 0 ? (
            messages.map((msg, index) => {
              const isSenderMe = msg.senderId === currentUser?.uid || msg.sender === currentUserFullName || msg.senderEmail?.toLowerCase() === currentUser?.email?.toLowerCase();
              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isSenderMe ? "flex-end" : "flex-start",
                    maxWidth: "80%",
                    alignSelf: isSenderMe ? "flex-end" : "flex-start"
                  }}
                >
                  {!isSenderMe && (
                    <span style={{ fontSize: "0.65rem", color: "var(--text-secondary)", marginBottom: "0.2rem", marginLeft: "0.25rem" }}>
                      {msg.sender}
                    </span>
                  )}
                  {msg.gifUrl ? (
                    <div style={{ maxWidth: "260px", borderRadius: "8px", overflow: "hidden", border: "1px solid var(--border-color)", backgroundColor: "rgba(0,0,0,0.2)" }}>
                      <img src={msg.gifUrl} alt="GIF" style={{ width: "100%", maxHeight: "200px", objectFit: "contain", display: "block" }} loading="lazy" />
                    </div>
                  ) : (
                    <div style={{
                      backgroundColor: isSenderMe ? "var(--primary)" : "rgba(255, 255, 255, 0.03)",
                      color: isSenderMe ? "#ffffff" : "var(--text-primary)",
                      padding: "0.55rem 0.85rem",
                      borderRadius: isSenderMe ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                      border: isSenderMe ? "none" : "1px solid var(--border-color)",
                      fontSize: "0.75rem",
                      lineHeight: "1.35",
                      wordBreak: "break-word"
                    }}>
                      {msg.text}
                    </div>
                  )}
                  <span style={{ fontSize: "0.55rem", color: "var(--text-secondary)", marginTop: "0.2rem" }}>
                    {msg.time}
                  </span>
                </div>
              );
            })
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "0.75rem", padding: "3rem 0", color: "var(--text-secondary)" }}>
              <AlertCircle size={24} style={{ opacity: 0.5 }} />
              <p style={{ margin: 0, fontSize: "0.8rem", textAlign: "center" }}>
                No active chat messages logged for this EST yet.
              </p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input / Footer */}
        <div style={{
          padding: "1rem 1.5rem",
          borderTop: "1px solid var(--border-color)",
          backgroundColor: "rgba(10, 12, 18, 0.4)"
        }}>
          <form 
            onSubmit={handleSendMessage}
            style={{ 
              display: "flex", 
              gap: "0.75rem",
              alignItems: "center"
            }}
          >
            <input
              type="text"
              placeholder="Send message to EST thread..."
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              style={{
                flex: 1,
                padding: "0.55rem 0.85rem",
                borderRadius: "4px",
                border: "1px solid var(--border-color)",
                backgroundColor: "var(--bg-input)",
                color: "var(--text-primary)",
                fontSize: "0.78rem"
              }}
            />
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={!messageInput.trim()}
              style={{ 
                padding: "0.55rem 1rem", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center" 
              }}
            >
              <Send size={14} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
